/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_exchange_challenge_token_for_auth_token } from '../models/Body_exchange_challenge_token_for_auth_token';
import type { Body_generate_email_challenge } from '../models/Body_generate_email_challenge';
import type { Body_import_field_values } from '../models/Body_import_field_values';
import type { Body_write_values } from '../models/Body_write_values';
import type { EntityRead } from '../models/EntityRead';
import type { ExcelAggBatchRequest } from '../models/ExcelAggBatchRequest';
import type { ExcelBatchRequest } from '../models/ExcelBatchRequest';
import type { ExcelBatchRequestTypeEnum } from '../models/ExcelBatchRequestTypeEnum';
import type { ExcelBatchResponse } from '../models/ExcelBatchResponse';
import type { ExcelCovenantBatchRequest } from '../models/ExcelCovenantBatchRequest';
import type { ExcelRelationshipBatchRequest } from '../models/ExcelRelationshipBatchRequest';
import type { ExcelTracebackRequest } from '../models/ExcelTracebackRequest';
import type { FieldRead } from '../models/FieldRead';
import type { FieldValueImportResponse } from '../models/FieldValueImportResponse';
import type { ImporterRunHistoryWithUser } from '../models/ImporterRunHistoryWithUser';
import type { ImportTemplatePayload } from '../models/ImportTemplatePayload';
import type { NamespaceBatchRequest } from '../models/NamespaceBatchRequest';
import type { NamespaceBatchRequestTypeEnum } from '../models/NamespaceBatchRequestTypeEnum';
import type { NamespaceBatchResponse } from '../models/NamespaceBatchResponse';
import type { NamespaceRead } from '../models/NamespaceRead';
import type { Token } from '../models/Token';
import type { Traceback } from '../models/Traceback';
import type { TrackedFieldScenarioRead } from '../models/TrackedFieldScenarioRead';
import type { TrackedPeriodTypeRead } from '../models/TrackedPeriodTypeRead';
import type { WriteBatchResponse } from '../models/WriteBatchResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ExcelService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Generate Email Challenge
     * Send user an excel login verification
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generateEmailChallenge(
        requestBody: Body_generate_email_challenge,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/generate-email-challenge',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Exchange Challenge Token For Auth Token
     * Called from excel to authenticate the client side
     * @param requestBody
     * @returns Token Successful Response
     * @throws ApiError
     */
    public exchangeChallengeTokenForAuthToken(
        requestBody: Body_exchange_challenge_token_for_auth_token,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/exchange-challenge-token-for-auth-token',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cluster Batch Operation
     * @param clusterId
     * @param operation
     * @param requestBody
     * @returns ExcelBatchResponse Successful Response
     * @throws ApiError
     */
    public clusterBatchOperation(
        clusterId: string,
        operation: ExcelBatchRequestTypeEnum,
        requestBody: Array<(ExcelBatchRequest | ExcelRelationshipBatchRequest | ExcelCovenantBatchRequest | ExcelAggBatchRequest)>,
    ): CancelablePromise<Array<ExcelBatchResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/entity/batch',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'operation': operation,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cluster Namespace Batch Operation
     * Process batched namespace operations from Excel.
     * @param clusterId
     * @param operation
     * @param requestBody
     * @returns NamespaceBatchResponse Successful Response
     * @throws ApiError
     */
    public clusterNamespaceBatchOperation(
        clusterId: string,
        operation: NamespaceBatchRequestTypeEnum,
        requestBody: Array<NamespaceBatchRequest>,
    ): CancelablePromise<Array<NamespaceBatchResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/namespace/batch',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'operation': operation,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * @deprecated
     * Batch Operation
     * @param operation
     * @param requestBody
     * @returns ExcelBatchResponse Successful Response
     * @throws ApiError
     */
    public batchOperation(
        operation: ExcelBatchRequestTypeEnum,
        requestBody: Array<(ExcelBatchRequest | ExcelRelationshipBatchRequest | ExcelCovenantBatchRequest | ExcelAggBatchRequest)>,
    ): CancelablePromise<Array<ExcelBatchResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/entity/batch',
            query: {
                'operation': operation,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * @deprecated
     * Namespace Batch Operation
     * @param operation
     * @param requestBody
     * @returns NamespaceBatchResponse Successful Response
     * @throws ApiError
     */
    public namespaceBatchOperation(
        operation: NamespaceBatchRequestTypeEnum,
        requestBody: Array<NamespaceBatchRequest>,
    ): CancelablePromise<Array<NamespaceBatchResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/namespace/batch',
            query: {
                'operation': operation,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity For Cluster
     * Get an entity by ID, scoped to a cluster.
     *
     * Returns 404 if the entity does not belong to the given cluster.
     * Returns 400 with ENTITY_WRONG_CLUSTER if the entity exists in a different
     * cluster that the user has access to (so they can switch).
     * ClusterReadGuard handles 403 if the user is not a member of the requested cluster.
     * @param clusterId
     * @param entityId
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public getEntityForCluster(
        clusterId: string,
        entityId: string,
    ): CancelablePromise<EntityRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/entities/{entity_id}',
            path: {
                'cluster_id': clusterId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespaces
     * Get all namespaces for a cluster.
     *
     * Returns an array of NamespaceRead objects.
     * @param clusterId
     * @returns NamespaceRead Successful Response
     * @throws ApiError
     */
    public listNamespaces(
        clusterId: string,
    ): CancelablePromise<Array<NamespaceRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-namespaces',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Fields
     * Get all fields for a cluster, optionally filtered by namespace.
     *
     * Parameters:
     * - namespace_id (optional): Filter fields by namespace
     *
     * Returns an array of FieldRead objects.
     * @param clusterId
     * @param namespaceId
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listFields(
        clusterId: string,
        namespaceId?: string,
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-fields',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tracked Period Types
     * Get all tracked period types for a cluster, optionally filtered by namespace.
     *
     * Parameters:
     * - namespace_id (optional): Filter tracked period types by namespace
     *
     * Returns an array of TrackedPeriodTypeRead objects.
     * @param clusterId
     * @param namespaceId
     * @returns TrackedPeriodTypeRead Successful Response
     * @throws ApiError
     */
    public listTrackedPeriodTypes(
        clusterId: string,
        namespaceId?: string,
    ): CancelablePromise<Array<TrackedPeriodTypeRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-tracked-period-types',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Scenarios
     * Get all field scenarios for a cluster, optionally filtered by namespace.
     *
     * Parameters:
     * - namespace_id (optional): Filter scenarios by namespace
     *
     * Returns an array of TrackedFieldScenarioRead objects.
     * @param clusterId
     * @param namespaceId
     * @returns TrackedFieldScenarioRead Successful Response
     * @throws ApiError
     */
    public listScenarios(
        clusterId: string,
        namespaceId?: string,
    ): CancelablePromise<Array<TrackedFieldScenarioRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-tracked-field-scenarios',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Import Field Values
     * Import field values from Excel.
     * @param clusterId
     * @param requestBody
     * @returns FieldValueImportResponse Successful Response
     * @throws ApiError
     */
    public importFieldValues(
        clusterId: string,
        requestBody: Body_import_field_values,
    ): CancelablePromise<FieldValueImportResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/import-field-values',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Field Importer Template
     * Create or update a field importer template.
     *
     * Args:
     * cluster_id: The cluster ID
     * template: The template to save
     *
     * Returns:
     * The saved template payload that excel plugin consumes
     * @param clusterId
     * @param requestBody
     * @returns ImportTemplatePayload Successful Response
     * @throws ApiError
     */
    public saveFieldImporterTemplate(
        clusterId: string,
        requestBody: ImportTemplatePayload,
    ): CancelablePromise<ImportTemplatePayload> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/save-import-template',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Field Importer Templates
     * List all field importer templates for a cluster.
     *
     * Args:
     * cluster_id: The cluster ID
     * import_template_ids: The list of template IDs
     *
     * Returns:
     * List of templates in payload form that excel plugin consumes
     * @param clusterId
     * @param importTemplateIds List of template IDs to filter by
     * @returns ImportTemplatePayload Successful Response
     * @throws ApiError
     */
    public listFieldImporterTemplates(
        clusterId: string,
        importTemplateIds?: Array<string>,
    ): CancelablePromise<Array<ImportTemplatePayload>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-import-templates',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'import_template_ids': importTemplateIds,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Importer Run History By Template
     * List all run history records for a template.
     *
     * Args:
     * import_template_id: The template ID
     *
     * Returns:
     * List of run history records with users
     * @param clusterId
     * @param importTemplateId List of template IDs to filter by
     * @returns ImporterRunHistoryWithUser Successful Response
     * @throws ApiError
     */
    public listImporterRunHistoryByTemplate(
        clusterId: string,
        importTemplateId?: string,
    ): CancelablePromise<Array<ImporterRunHistoryWithUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/excel/clusters/{cluster_id}/list-import-runs',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'import_template_id': importTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Write Values
     * Submit batch writes from Excel LX.WRITE formulas.
     * @param clusterId
     * @param requestBody
     * @returns WriteBatchResponse Successful Response
     * @throws ApiError
     */
    public writeValues(
        clusterId: string,
        requestBody: Body_write_values,
    ): CancelablePromise<WriteBatchResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/write',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Traceback Formula
     * Get traceback for an LX expression from Excel.
     * Resolves entity by name/ID and traces expression evaluation.
     * @param clusterId
     * @param requestBody
     * @returns Traceback Successful Response
     * @throws ApiError
     */
    public tracebackFormula(
        clusterId: string,
        requestBody: ExcelTracebackRequest,
    ): CancelablePromise<Traceback> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/excel/clusters/{cluster_id}/traceback-formula',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
